import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import { api } from '../../../../services/api';
import { cardSx,
  sectionHeaderSx,
  headerLeftSx,
  cardContentSx,
  loadingBoxSx,
  addressCardSx,
  dialogTitleSx,
  dialogContentSx,
  dialogActionsSx,
  selectFieldSx,
  selectMenuPaperSx,
  textFieldSx,
  addressFieldSx,
  addButtonSx,
} from '../../styles/TripAddressSection.styles';

interface Address {
  id?: number;
  addressType: 'PICKUP' | 'DROP';
  location?: string;
  address: string | null;
  city?: string;
  landmark?: string;
}

interface TripAddressesSectionProps {
  addresses: Address[];
  loading?: boolean;
  tripCode: string;
  onUpdate: () => Promise<void>;
  onRefreshTripDetails: () => Promise<void>;
  disabled?: boolean;
  tripDetail?:any;
  sourceCity?:string;
  destinationCity?:string;
  // hasDriverAssigned?: boolean;
}

const TripAddressesSection: React.FC<TripAddressesSectionProps> = ({
  addresses,
  loading,
  tripCode,
  onUpdate,
  onRefreshTripDetails,
  disabled = false,
  tripDetail,
  sourceCity,
  destinationCity,
  // hasDriverAssigned = false
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localAddresses, setLocalAddresses] = useState<Address[]>(addresses);
  const [loadingNewAddress, setLoadingNewAddress] = useState(false);
  
  // Add ref for scroll position preservation
  const addressesSectionRef = useRef<HTMLDivElement>(null);
  const [shouldPreserveScroll, setShouldPreserveScroll] = useState(false);
  
  const [formData, setFormData] = useState<Address>({
    addressType: 'PICKUP',
    location: '',
    address: '',
    city: '',
    landmark: ''
  });

const isAirportTransfer = tripDetail?.serviceId === 1;
const isMumbaiAirportDrop = destinationCity === 'Mumbai Airport' || destinationCity === 'Mumbai'; //drop to Mumbai Airport  only pickup address is required
const isMumbaiAirportPickup = sourceCity === 'Mumbai Airport' || sourceCity === 'Mumbai'; //pickup from Mumbai Airport  flight detail + drop address

const shouldShowAddressField = () => {
  // Only apply special Mumbai Airport logic for airport transfer service (serviceId == 1)
  if (!isAirportTransfer) {
    return true; // Always show address field for non-airport services
  }
  
  // For airport transfer service, hide address field only for DROP type in Mumbai Airport drop scenario
  if (formData.addressType === 'DROP' && isMumbaiAirportDrop) {
    return false;
  }
  
  return true;
};

const getAddressFieldLabel = () => {
  if (!isAirportTransfer) {
    return "Address";
  } 
  if (formData.addressType === 'PICKUP') {
    if (isMumbaiAirportDrop) {
      return "Address";
    }
    if (isMumbaiAirportPickup) {
      return "Flight Number";
    }
  }
  
  if (formData.addressType === 'DROP') {
    if (isMumbaiAirportPickup) {
      return "Address";
    }
    if (isMumbaiAirportDrop) {
      return "Address";
    }
  }
  
  return "Address";
};

const getAddressFieldPlaceholder = () => {
  if (!isAirportTransfer) {
    return "Enter complete address";
  }
  
  if (formData.addressType === 'PICKUP') {
    if (isMumbaiAirportDrop) {
      return "Enter complete address";
    }
    if (isMumbaiAirportPickup) {
      return "Enter flight number";
    }
  }
  
  if (formData.addressType === 'DROP') {
    return "Enter drop-off address";
  } 
  return "Enter complete address";
};

const shouldShowTerminalOptions = () => {
  if(!isAirportTransfer) {
    return false;
  }

  if(isMumbaiAirportPickup && formData.addressType === 'PICKUP'){
    return true;
  }
  if(isMumbaiAirportDrop && formData.addressType === 'DROP'){
    return true;
  }
  return false;
}

const getLocationFieldLabel = () => {
  if (shouldShowTerminalOptions()) {
    return "Terminal";
  }
  return "Location";
};
const getDisplayAddressLabel = (address: Address) => {
  if (!isAirportTransfer) {
    return "Address";
  }
  
  // For Mumbai Airport service type
  if (address.addressType === 'PICKUP') {
    if (isMumbaiAirportDrop) {
      return "Address";
    }
    if (isMumbaiAirportPickup) {
      return "Flight Number";
    }
  }
  
  if (address.addressType === 'DROP') {
    if (isMumbaiAirportPickup) {
      return "Address";
    }
    if (isMumbaiAirportDrop) {
      return "Address";
    }
  }
  
  return "Address";
};
  // Update local addresses when props change
  useEffect(() => {
    setLocalAddresses(addresses);
  }, [addresses]);

  // Preserve scroll position after operations complete
  useEffect(() => {
    if (shouldPreserveScroll && addressesSectionRef.current) {
      // Use requestAnimationFrame for better timing with DOM updates
      const scrollToSection = () => {
        if (addressesSectionRef.current) {
          addressesSectionRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
          setShouldPreserveScroll(false);
        }
      };
         // Double requestAnimationFrame to ensure DOM is fully updated
      requestAnimationFrame(() => {
        requestAnimationFrame(scrollToSection);
      });
    }
  }, [shouldPreserveScroll]);
  
  // Separate effect to handle scroll preservation after state changes
  useEffect(() => {
    if (shouldPreserveScroll && !saving && !loadingNewAddress && !dialogOpen) {
      // Trigger scroll after operations are complete
      const timer = setTimeout(() => {
        if (addressesSectionRef.current && shouldPreserveScroll) {
          addressesSectionRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
          setShouldPreserveScroll(false);
        }
      }, 200); // Slightly longer delay to ensure all updates are complete
      
      return () => clearTimeout(timer);
    }
  }, [localAddresses, saving, loadingNewAddress, dialogOpen, shouldPreserveScroll]);
  
  // Add helper functions to check existing addresses
  const hasPickupAddress = localAddresses.some(addr => addr.addressType === 'PICKUP');
  const hasDropAddress = localAddresses.some(addr => addr.addressType === 'DROP');
  const hasBothAddressTypes = hasPickupAddress && hasDropAddress;

  // Get available address types for dropdown
  const getAvailableAddressTypes = () => {
    const options = [];
    
    // In edit mode, only show the current address type being edited
    if (isEditMode && editingAddress) {
      const currentType = editingAddress.addressType;
      const label = currentType === 'PICKUP' ? 'Pickup' : 'Drop-off';
      options.push({ value: currentType, label });
      return options;
    }
    
    // In add mode, show only address types that don't already exist
    if (!hasPickupAddress) {
      options.push({ value: 'PICKUP', label: 'Pickup' });
    }
    if (!hasDropAddress) {
      options.push({ value: 'DROP', label: 'Drop-off' });
    }
    return options;
  };

  const handleAddAddress = () => {
    // Set default address type to first available option
    const availableTypes = getAvailableAddressTypes();
    const defaultType = availableTypes.length > 0 ? availableTypes[0].value as 'PICKUP' | 'DROP' : 'PICKUP';
    
    setFormData({
      addressType: defaultType,
      location: '',
      address: '',
      city: '',
      landmark: ''
    });
    setEditingAddress(null);
    setIsEditMode(false);
    setError(null);
    setDialogOpen(true);
  };

  const handleEditAddress = (address: Address) => {
    setFormData({ ...address });
    setEditingAddress(address);
    setIsEditMode(true);
    setError(null);
    setDialogOpen(true);
  };

  const handleDeleteAddress = async (addressId: number) => {
    if (!window.confirm('Are you sure you want to delete this address?')) {
      return;
    }
    // Store original addresses for rollback
    const originalAddresses = [...localAddresses];
    try {
      setSaving(true);
      setError(null);
      setShouldPreserveScroll(true); // Set flag to preserve scroll
       // Optimistically remove address from local state
      setLocalAddresses(prev => prev.filter(addr => addr.id !== addressId));     
      await api.removeTripAddress(tripCode, addressId);
        // Clear cache to ensure fresh data on next navigation
      api.clearCache(`getTripDetails_${tripCode}`);
      api.clearCache('getAllTrips');
      
    } catch (error: any) {
      // Revert optimistic update on error
      setLocalAddresses(originalAddresses);
      console.error('Error deleting address:', error);
      setError(error.message || 'Failed to delete address');
      setShouldPreserveScroll(false); // Reset flag on error
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAddress = async () => {
    let originalAddress: Address | null = null;
    
    try {
      setSaving(true);
      setError(null);
      setShouldPreserveScroll(true); // Set flag to preserve scroll

      // Validate required fields
      const errors = [];
      
      if (!formData.addressType) {
        errors.push('Address Type');
      } 
      if (!formData.location || formData.location.trim() === '') {
        errors.push('Location');
      }  
      if (shouldShowAddressField()) {
        if (!formData.address || formData.address.trim() === '') {
          errors.push('Address');
        }
      }    
      if (errors.length > 0) {
        // setError(`Please fill in the following required fields: ${errors.join(', ')}`);
        setError('Please complete all mandatory fields before proceeding.');
        setShouldPreserveScroll(false); // Reset flag on validation error
        return;
      }
      // Create address data in the new simplified format
      const addressData = {
        addressType: formData.addressType,
        location: formData.location || undefined,
        address: shouldShowAddressField() ? formData.address : "N/A" // Send empty string if address field is hidden
      };

      if (isEditMode && editingAddress?.id) {
        // Editing existing address
        originalAddress = editingAddress;
        const updatedAddress = {
          ...editingAddress,
          ...addressData
        };

        // Optimistically update the address in local state
        setLocalAddresses(prev => prev.map(address => 
          address.id === editingAddress.id ? updatedAddress : address
        ));

        // Close dialog and reset form
        setDialogOpen(false);
        setEditingAddress(null);
        setIsEditMode(false);
        setError(null);

        // Make API call to update existing address with type assertion
        await api.updateTripAddress(tripCode, editingAddress.id, addressData as any);
        
        // Clear cache to ensure fresh data on next navigation
        api.clearCache(`getTripDetails_${tripCode}`);
        api.clearCache('getAllTrips');
      } else {
        // Adding new address
        // Close dialog and reset form first
        setDialogOpen(false);
        setEditingAddress(null);
        setIsEditMode(false);
        setError(null);

        // Show loading state for new address
        setLoadingNewAddress(true);

        // Make API call to add new address with type assertion
        const addedAddress = await api.addTripAddress(tripCode, addressData as any);
        
        // Clear cache to ensure fresh data
        api.clearCache(`getTripDetails_${tripCode}`);
        api.clearCache('getAllTrips');

        // Check if we need fresh data from server
        if (!addedAddress || typeof addedAddress !== 'object' || !('id' in addedAddress)) {
          
          if (onRefreshTripDetails) {
            await onRefreshTripDetails();
          } else {
            // Fallback: try to refresh from server directly
            try {
              const freshTripData = await api.getTripDetails(tripCode);
              if (freshTripData && typeof freshTripData === 'object' && 'addresses' in freshTripData) {
                const freshAddresses = (freshTripData as any).addresses;
                if (Array.isArray(freshAddresses)) {
                  setLocalAddresses(freshAddresses);
                  
                }
              }
            } catch (refreshError) {
              console.error('Could not refresh address data:', refreshError);
              alert('Address added but could not refresh the list. Please reload the page.');
            }
          }
        } else {
          // Add the new address with real ID from server to local state
          const newAddressWithRealId = {
            id: (addedAddress as any).id,
            ...addressData
          };
          setLocalAddresses(prev => [...prev, newAddressWithRealId]);
        }
        
        // Hide loading state
        setLoadingNewAddress(false);
      }
      
    } catch (error: any) {
      // Hide loading state on error
      setLoadingNewAddress(false);
      
      // Revert optimistic update on error (only for edit operations)
      if (isEditMode && editingAddress && originalAddress) {
        // Restore original address for edit operations
        setLocalAddresses(prev => prev.map(address => 
          address.id === editingAddress.id ? originalAddress! : address
        ));
      }
      // For add operations, no rollback needed since we don't do optimistic updates
      
      console.error('Error saving address:', error);
      setError(error.message || 'Failed to save address');
      setShouldPreserveScroll(false); // Reset flag on error
    } finally {
      setSaving(false);
      setLoadingNewAddress(false); // Ensure loading state is cleared
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingAddress(null);
    setIsEditMode(false);
    setError(null);
  };

  const handleInputChange = (field: keyof Address) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSelectChange = (field: keyof Address) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getAddressTypeColor = (type: 'PICKUP' | 'DROP') => {
    return type === 'PICKUP' ? 'success' : 'primary';
  };

  const getAddressTypeIcon = (type: 'PICKUP' | 'DROP') => {
    return <LocationIcon fontSize="small" />;
  };

  const SectionHeader = () => (
    <Box 
      sx={sectionHeaderSx}
    >
      <Box sx={headerLeftSx}>
        <HomeIcon />
        <Typography variant="h6" fontWeight="bold">
          TRIP ADDRESSES ({localAddresses.length})
        </Typography>
      </Box>
      <Box sx={headerLeftSx}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddAddress}
          disabled={loading || saving || loadingNewAddress || disabled || hasBothAddressTypes}
          sx={addButtonSx}
        >
          {loadingNewAddress ? 'ADDING...' : 'ADD ADDRESS'}
        </Button>
      </Box>
    </Box>
  );

  return (
    <Card ref={addressesSectionRef} sx={cardSx}>
      <SectionHeader />
      <CardContent sx={cardContentSx}>
        {loadingNewAddress ? (
          // Show loading state when adding new address
          <Box sx={loadingBoxSx}>
            <CircularProgress size={40} sx={{ color: '#2563eb', mb: 2 }} />
            <Typography variant="body1" color="white">
              Adding address...
            </Typography>
          </Box>
        ) : localAddresses.length === 0 ? (
          <Typography variant="body2" color="textSecondary" textAlign="center" sx={{ py: 4 }}>
            No addresses added yet. Click "Add Address" to add pickup or drop-off locations.
          </Typography>
        ) : (
          <Box display="flex" flexWrap="wrap" gap={2}>
            {localAddresses.sort((a, b) => {
              // PICKUP addresses come first
              if (a.addressType === 'PICKUP' && b.addressType === 'DROP') return -1;
              if (a.addressType === 'DROP' && b.addressType === 'PICKUP') return 1;
              return 0;
    }).map((address, index) => (
              <Box 
                key={address.id || index}
                sx={{ 
                  flexBasis: { xs: '100%', md: 'calc(50% - 8px)' },
                  minWidth: 0
                }}
              >
                <Card sx={addressCardSx}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          icon={getAddressTypeIcon(address.addressType)}
                          label={address.addressType}
                          color={getAddressTypeColor(address.addressType)}
                          size="small"
                        />
                      </Box>
                      <Box display="flex" gap={1}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditAddress(address)}
                          disabled={saving || loadingNewAddress || disabled}
                          sx={{ color: 'white' }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        {address.id && (
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteAddress(address.id!)}
                            disabled={saving || loadingNewAddress || disabled}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </Box>

                    <Typography variant="body2" gutterBottom sx={{ color: 'white' }}>
                        <strong>Location:</strong> {address.location}
                      </Typography>
                    {(address.addressType !== 'DROP' || !isMumbaiAirportDrop || !isAirportTransfer) && (
                        <Typography variant="body2" gutterBottom sx={{ color: 'white' }}>
                          <strong>{getDisplayAddressLabel(address)}:</strong> {address.address}
                        </Typography>
                      )}
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>

      {/* Add/Edit Address Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={dialogTitleSx}>
          {isEditMode ? 'Edit Address' : 'Add New Address'}
        </DialogTitle>
        <DialogContent sx={dialogContentSx}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box sx={{ mt: 1 }}>
            <Box sx={{ mb: 2 }}>
              <TextField
                select
                label="Address Type"
                value={formData.addressType}
                onChange={handleSelectChange('addressType')}
                fullWidth
                required
                sx={selectFieldSx}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: selectMenuPaperSx
                    }
                  }
                }}
              >
                {getAvailableAddressTypes().map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            
            <Box sx={{ mb: 2 }}>
            {shouldShowTerminalOptions() ? (
              <TextField
                select
                label={getLocationFieldLabel()}
                value={formData.location || ''}
                onChange={handleInputChange('location')}
                fullWidth
                required
                sx={selectFieldSx}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: selectMenuPaperSx
                    }
                  }
                }}
              >
                <MenuItem value="Terminal 1">Terminal 1</MenuItem>
                <MenuItem value="Terminal 2">Terminal 2</MenuItem>
              </TextField>
            ) : (
              <TextField
                label="Location"
                value={formData.location || ''}
                onChange={handleInputChange('location')}
                fullWidth
                placeholder="Enter location name"
                sx={textFieldSx}
              />
            )}
            </Box>
            
            {shouldShowAddressField() && (<Box sx={{ mb: 2 }}>
              <TextField
                label={getAddressFieldLabel()}
                // label={(isMumbaiAirportDrop && isAirportTransfer ) ? "Pickup Address" : isMumbaiAirportPickup ? "Flight Details" : "Address"}
                value={formData.address}
                onChange={handleInputChange('address')}
                fullWidth
                required
                multiline
                rows={3}
                placeholder={getAddressFieldPlaceholder()}
                sx={addressFieldSx}
              />
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                {formData.addressType === 'PICKUP' && isMumbaiAirportPickup && isAirportTransfer
                  ? "Please provide flight number."
                  : "Please provide a complete address."}
              </Typography>
            </Box>)}
          </Box>
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button 
            onClick={handleCloseDialog} 
            disabled={saving || loadingNewAddress}
            sx={{ color: 'white' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveAddress}
            variant="contained"
            disabled={saving || loadingNewAddress}
            sx={{ backgroundColor: '#3b82f6' }}
          >
            {saving || loadingNewAddress ? <CircularProgress size={20} /> : (isEditMode ? 'Update Address' : 'Add Address')}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default TripAddressesSection;